import React, { useState, useEffect } from "react";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Building2,
  Users,
  UserPlus,
  Briefcase,
  Calendar,
  AlertTriangle,
  Palette,
  Shield,
  Plus,
  Eye,
  ChevronRight,
  Activity,
  TrendingUp,
  Upload,
  X,
  Loader2,
  Key,
} from "lucide-react";

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [multiOrgCustomers, setMultiOrgCustomers] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Modal states
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showBranding, setShowBranding] = useState(false);
  const [brandingOrg, setBrandingOrg] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // Form states
  const [newOrg, setNewOrg] = useState({
    name: "",
    description: "",
    owner_email: "",
    owner_name: "",
    owner_password: ""
  });
  const [newUser, setNewUser] = useState({
    email: "",
    name: "",
    password: "",
    organization_id: "",
    role: "member"
  });
  const [branding, setBranding] = useState({
    logo_url: "",
    primary_color: "#3B82F6",
    secondary_color: "#1E40AF",
    accent_color: "#10B981",
    font_family: "Inter, sans-serif",
    company_name: "",
    website_url: "",
    footer_text: ""
  });
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a valid image file (JPEG, PNG, GIF, WebP, SVG)");
      return;
    }
    
    // Validate file size (max 2MB for logos)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo file size should be less than 2MB");
      return;
    }
    
    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Construct full URL for the logo
      const logoUrl = response.data.url.startsWith('/api') 
        ? `${API.replace('/api', '')}${response.data.url}`
        : response.data.url;
      
      setBranding({ ...branding, logo_url: logoUrl });
      toast.success("Logo uploaded successfully");
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error("Failed to upload logo");
    }
    setIsUploadingLogo(false);
  };

  useEffect(() => {
    if (user?.is_master_admin) {
      fetchAllData();
    }
  }, [user]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, orgsRes, usersRes, customersRes, multiOrgRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/organizations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/customers/multi-org`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setStats(statsRes.data);
      setOrganizations(orgsRes.data);
      setAllUsers(usersRes.data);
      setAllCustomers(customersRes.data);
      setMultiOrgCustomers(multiOrgRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    }
    setIsLoading(false);
  };

  const fetchOrgUsers = async (orgId) => {
    try {
      const response = await axios.get(`${API}/admin/organizations/${orgId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgUsers(response.data);
    } catch (error) {
      toast.error("Failed to load organization users");
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/organizations`, newOrg, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Organization created successfully");
      setShowCreateOrg(false);
      setNewOrg({ name: "", description: "", owner_email: "", owner_name: "", owner_password: "" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create organization");
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User created successfully");
      setShowCreateUser(false);
      setNewUser({ email: "", name: "", password: "", organization_id: "", role: "member" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleUpdateBranding = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/branding/${brandingOrg.id}`, branding, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Branding updated successfully");
      setShowBranding(false);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update branding");
    }
  };

  const openPasswordModal = (userToUpdate) => {
    setPasswordUser(userToUpdate);
    setNewPassword("");
    setConfirmPassword("");
    setShowPasswordModal(true);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    try {
      await axios.put(`${API}/admin/users/${passwordUser.id}/password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password updated for ${passwordUser.name}`);
      setShowPasswordModal(false);
      setNewPassword("");
      setConfirmPassword("");
      setPasswordUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    }
  };

  const handlePromoteUser = async (userId) => {
    if (!window.confirm("Promote this user to Master Admin?")) return;
    try {
      await axios.post(`${API}/admin/promote/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User promoted to Master Admin");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to promote user");
    }
  };

  const handleDemoteUser = async (userId) => {
    if (!window.confirm("Remove Master Admin privileges from this user?")) return;
    try {
      await axios.post(`${API}/admin/demote/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Master Admin privileges removed");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to demote user");
    }
  };

  const openBrandingModal = (org) => {
    setBrandingOrg(org);
    setBranding(org.branding || {
      logo_url: "",
      primary_color: "#3B82F6",
      secondary_color: "#1E40AF",
      accent_color: "#10B981",
      font_family: "Inter, sans-serif",
      company_name: org.name,
      website_url: "",
      footer_text: `© ${new Date().getFullYear()} ${org.name}`
    });
    setShowBranding(true);
  };

  if (!user?.is_master_admin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Shield className="w-5 h-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You need Master Admin privileges to access this page.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Master Admin Dashboard</h1>
          <p className="text-slate-500">Manage organizations, users, and system settings</p>
        </div>
        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
          <Shield className="w-3 h-3 mr-1" />
          Master Admin
        </Badge>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_organizations}</p>
                  <p className="text-xs text-slate-500">Organizations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_users}</p>
                  <p className="text-xs text-slate-500">Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_customers}</p>
                  <p className="text-xs text-slate-500">Customers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_sessions}</p>
                  <p className="text-xs text-slate-500">Sessions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Activity className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active_sessions_today}</p>
                  <p className="text-xs text-slate-500">Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.customers_multi_org > 0 ? "border-amber-300 bg-amber-50" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stats.customers_multi_org > 0 ? "bg-amber-200" : "bg-slate-100"}`}>
                  <AlertTriangle className={`w-5 h-5 ${stats.customers_multi_org > 0 ? "text-amber-600" : "text-slate-600"}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.customers_multi_org}</p>
                  <p className="text-xs text-slate-500">Multi-Org</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="organizations" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="organizations">Organizations</TabsTrigger>
          <TabsTrigger value="users">All Users</TabsTrigger>
          <TabsTrigger value="customers">Customers</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
        </TabsList>

        {/* Organizations Tab */}
        <TabsContent value="organizations" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Organizations</h2>
            <Button data-testid="create-org-btn" onClick={() => setShowCreateOrg(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Organization
            </Button>
          </div>

          <div className="grid gap-4">
            {organizations.map((org) => (
              <Card key={org.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: org.branding?.primary_color || "#3B82F6" }}
                      >
                        {org.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{org.name}</h3>
                        <p className="text-sm text-slate-500">{org.description || "No description"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-lg font-semibold">{org.member_count}</p>
                        <p className="text-xs text-slate-500">Users</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold">{org.customer_count}</p>
                        <p className="text-xs text-slate-500">Customers</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold">{org.session_count}</p>
                        <p className="text-xs text-slate-500">Sessions</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedOrg(org);
                            fetchOrgUsers(org.id);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Users
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openBrandingModal(org)}
                        >
                          <Palette className="w-4 h-4 mr-1" />
                          Branding
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* All Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">All Users</h2>
            <Button data-testid="create-user-btn" onClick={() => setShowCreateUser(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Master Admin</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allUsers.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      {u.organization_name || (
                        <span className="text-slate-400">No organization</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{u.organization_role || "N/A"}</Badge>
                    </TableCell>
                    <TableCell>
                      {u.is_master_admin && (
                        <Badge className="bg-purple-100 text-purple-700">
                          <Shield className="w-3 h-3 mr-1" />
                          Master Admin
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openPasswordModal(u)}
                          title="Change Password"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        {u.is_master_admin ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDemoteUser(u.id)}
                            disabled={u.id === user.id}
                          >
                            Demote
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePromoteUser(u.id)}
                          >
                            Promote
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-4">
          <h2 className="text-lg font-semibold">All Customers</h2>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer Name</TableHead>
                  <TableHead>Contact Person</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Sessions</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allCustomers.map((c) => (
                  <TableRow key={c.id} className={c.is_multi_org ? "bg-amber-50" : ""}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{c.contact_person || "-"}</TableCell>
                    <TableCell>{c.organization_name}</TableCell>
                    <TableCell>{c.session_count}</TableCell>
                    <TableCell>
                      {c.is_multi_org && (
                        <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Multi-Org
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <h2 className="text-lg font-semibold">Multi-Organization Customers</h2>
          <p className="text-sm text-slate-500">
            Customers that appear in multiple organizations. This may indicate duplicate entries or shared customers.
          </p>
          
          {multiOrgCustomers.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <TrendingUp className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-slate-600">No multi-organization customers found</p>
                <p className="text-sm text-slate-400">All customers are unique to their organizations</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {multiOrgCustomers.map((item, idx) => (
                <Card key={idx} className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      {item.customer_name}
                    </CardTitle>
                    <CardDescription>
                      Found in {item.organization_count} organizations
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {item.organizations.map((org, orgIdx) => (
                        <div key={orgIdx} className="flex items-center gap-3 p-2 bg-white rounded-lg">
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{org.organization_name}</span>
                          {org.contact_person && (
                            <span className="text-sm text-slate-500">
                              (Contact: {org.contact_person})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Organization Users Modal */}
      <Dialog open={!!selectedOrg} onOpenChange={() => setSelectedOrg(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Users in {selectedOrg?.name}</DialogTitle>
            <DialogDescription>
              {orgUsers.length} users in this organization
            </DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orgUsers.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{u.role}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {u.joined_at ? new Date(u.joined_at).toLocaleDateString() : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      {/* Branding Modal */}
      <Dialog open={showBranding} onOpenChange={setShowBranding}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Branding Configuration</DialogTitle>
            <DialogDescription>
              Customize the look and feel for {brandingOrg?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateBranding} className="space-y-4">
            {/* Logo Upload */}
            <div>
              <Label>Company Logo</Label>
              <div className="mt-2 space-y-3">
                {branding.logo_url ? (
                  <div className="relative inline-block">
                    <img 
                      src={branding.logo_url} 
                      alt="Company Logo" 
                      className="h-16 w-auto object-contain rounded border bg-white p-1"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <button
                      type="button"
                      onClick={() => setBranding({ ...branding, logo_url: "" })}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100 border-slate-300">
                      <div className="flex flex-col items-center justify-center pt-2 pb-2">
                        {isUploadingLogo ? (
                          <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-6 h-6 text-slate-400 mb-1" />
                            <p className="text-xs text-slate-500">Click to upload logo</p>
                            <p className="text-[10px] text-slate-400">PNG, JPG, SVG (max 2MB)</p>
                          </>
                        )}
                      </div>
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={isUploadingLogo}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <Label>Company Name</Label>
              <Input
                value={branding.company_name || ""}
                onChange={(e) => setBranding({ ...branding, company_name: e.target.value })}
                className="h-9"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Primary</Label>
                <div className="flex gap-1 mt-1">
                  <Input
                    type="color"
                    value={branding.primary_color}
                    onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                    className="w-10 h-8 p-0.5 cursor-pointer"
                  />
                  <Input
                    value={branding.primary_color}
                    onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                    className="flex-1 h-8 text-xs"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Secondary</Label>
                <div className="flex gap-1 mt-1">
                  <Input
                    type="color"
                    value={branding.secondary_color}
                    onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                    className="w-10 h-8 p-0.5 cursor-pointer"
                  />
                  <Input
                    value={branding.secondary_color}
                    onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                    className="flex-1 h-8 text-xs"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Accent</Label>
                <div className="flex gap-1 mt-1">
                  <Input
                    type="color"
                    value={branding.accent_color}
                    onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                    className="w-10 h-8 p-0.5 cursor-pointer"
                  />
                  <Input
                    value={branding.accent_color}
                    onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                    className="flex-1 h-8 text-xs"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label>Font Family</Label>
              <Select
                value={branding.font_family}
                onValueChange={(value) => setBranding({ ...branding, font_family: value })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Inter, sans-serif">Inter</SelectItem>
                  <SelectItem value="Roboto, sans-serif">Roboto</SelectItem>
                  <SelectItem value="Open Sans, sans-serif">Open Sans</SelectItem>
                  <SelectItem value="Poppins, sans-serif">Poppins</SelectItem>
                  <SelectItem value="Montserrat, sans-serif">Montserrat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Website URL</Label>
              <Input
                value={branding.website_url || ""}
                onChange={(e) => setBranding({ ...branding, website_url: e.target.value })}
                placeholder="https://ipecuk.com"
                className="h-9"
              />
              <p className="text-xs text-slate-500 mt-1">Displayed in presentation mode header</p>
            </div>
            
            <div>
              <Label>Footer Text</Label>
              <Input
                value={branding.footer_text || ""}
                onChange={(e) => setBranding({ ...branding, footer_text: e.target.value })}
                placeholder="© 2024 Company Name"
                className="h-9"
              />
            </div>
            
            {/* Preview */}
            <div className="pt-3 border-t">
              <Label className="text-xs text-slate-500">Preview</Label>
              <div 
                className="mt-2 p-3 rounded-lg border"
                style={{ 
                  backgroundColor: branding.primary_color + "10",
                  borderColor: branding.primary_color,
                  fontFamily: branding.font_family
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  {branding.logo_url ? (
                    <img 
                      src={branding.logo_url} 
                      alt="Logo" 
                      className="h-8 w-auto object-contain"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ) : (
                    <div 
                      className="w-8 h-8 rounded flex items-center justify-center text-white font-bold text-sm"
                      style={{ backgroundColor: branding.primary_color }}
                    >
                      {branding.company_name?.charAt(0) || "A"}
                    </div>
                  )}
                  <div className="flex flex-col">
                    <span style={{ color: branding.secondary_color }} className="font-semibold text-sm">
                      {branding.company_name || "Company Name"}
                    </span>
                    {branding.website_url && (
                      <span className="text-xs opacity-60">
                        {branding.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs" style={{ color: branding.accent_color }}>
                  {branding.footer_text || "Footer text preview"}
                </p>
              </div>
            </div>
            
            <Button type="submit" className="w-full">Save Branding</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Organization Modal */}
      <Dialog open={showCreateOrg} onOpenChange={setShowCreateOrg}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>
              Create a new organization. Optionally create an owner account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateOrg} className="space-y-4">
            <div>
              <Label>Organization Name *</Label>
              <Input
                value={newOrg.name}
                onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={newOrg.description}
                onChange={(e) => setNewOrg({ ...newOrg, description: e.target.value })}
              />
            </div>
            <div className="border-t pt-4">
              <p className="text-sm text-slate-500 mb-3">Owner Account (Optional)</p>
              <div className="space-y-3">
                <div>
                  <Label>Owner Email</Label>
                  <Input
                    type="email"
                    value={newOrg.owner_email}
                    onChange={(e) => setNewOrg({ ...newOrg, owner_email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Owner Name</Label>
                  <Input
                    value={newOrg.owner_name}
                    onChange={(e) => setNewOrg({ ...newOrg, owner_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Owner Password</Label>
                  <Input
                    type="password"
                    value={newOrg.owner_password}
                    onChange={(e) => setNewOrg({ ...newOrg, owner_password: e.target.value })}
                    placeholder="Default: changeme123"
                  />
                </div>
              </div>
            </div>
            <Button type="submit" className="w-full">Create Organization</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>
              Create a new user and assign to an organization.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Name *</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Organization *</Label>
              <select
                value={newUser.organization_id}
                onChange={(e) => setNewUser({ ...newUser, organization_id: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              >
                <option value="">Select organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Role</Label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            <Button type="submit" className="w-full">Create User</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Update Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Update password for {passwordUser?.name} ({passwordUser?.email})
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div>
              <Label>New Password *</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                minLength={6}
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
            </div>
            <div>
              <Label>Confirm Password *</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>
            <Button type="submit" className="w-full">Update Password</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
